/**
 * FRENTE DE SERVIÇO — regras puras.
 *
 * A Frente pertence a UM PROJETO. O Cliente não é vínculo da Frente: ele vem
 * pela relação CLIENTE → PROJETO → FRENTE. O `clientId` guardado no registro
 * existe só como partição do repositório e é DERIVADO do projeto, nunca
 * informado pelo usuário.
 */

import { AppError, ErrorCode } from "../../shared/errors/app-error.js";

export type ServiceFrontStatus = "ACTIVE" | "INACTIVE";

export interface ServiceFrontInput {
  projectId: string;
  code: string;
  name: string;
  description?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  notes?: string | null;
  status?: ServiceFrontStatus;
}

/** Frente com o projeto já resolvido, como a listagem precisa exibir. */
export interface ResolvedServiceFront {
  id: string;
  projectId: string | null;
  projectName: string;
  clientId: string;
  clientName: string;
  code: string;
  name: string;
  description: string | null;
  startDate: string | null;
  endDate: string | null;
  notes: string | null;
  status: ServiceFrontStatus;
}

export const FRONT_REQUIRES_PROJECT = "Selecione o Projeto da Frente de Serviço.";
export const FRONT_REQUIRES_CLIENT = "Selecione o Cliente da Frente de Serviço.";
export const FRONT_PROJECT_OF_OTHER_CLIENT = "O Projeto selecionado não pertence ao Cliente informado.";
export const FRONT_CLIENT_IS_FIXED = "O Cliente de uma Frente de Serviço já cadastrada não pode ser alterado.";
export const FRONT_REQUIRES_CODE = "Informe o Código da Frente de Serviço.";
export const FRONT_REQUIRES_NAME = "Informe o Nome da Frente de Serviço.";
export const FRONT_IN_USE_MESSAGE =
  "Esta Frente de Serviço já possui apontamentos de RDO registrados e não pode ser excluída. Inative-a para impedir novos apontamentos sem apagar o histórico.";

/**
 * Uma Frente não existe solta: sem Projeto, sem Código e sem Nome não há
 * cadastro. As datas, quando ambas informadas, precisam ser coerentes.
 */
export function validateServiceFrontInput(input: ServiceFrontInput): void {
  if (!input.projectId?.trim()) {
    throw new AppError(ErrorCode.Validation, "Service front requires a project", { safeMessage: FRONT_REQUIRES_PROJECT });
  }
  if (!input.code?.trim()) {
    throw new AppError(ErrorCode.Validation, "Service front requires a code", { safeMessage: FRONT_REQUIRES_CODE });
  }
  if (!input.name?.trim()) {
    throw new AppError(ErrorCode.Validation, "Service front requires a name", { safeMessage: FRONT_REQUIRES_NAME });
  }
  if (input.startDate && input.endDate && input.endDate < input.startDate) {
    throw new AppError(ErrorCode.Validation, "Service front end date precedes start date", {
      safeMessage: "A Data de Término da Frente não pode ser anterior à Data de Início."
    });
  }
}

/**
 * Frentes de um projeto. Registros antigos, cadastrados antes do vínculo com
 * Projeto, só aparecem quando o projeto foi resolvido a partir da OS deles —
 * nunca "por padrão", para não vazar Frente de um projeto para outro.
 */
export function filterFrontsByProject<T extends { projectId?: string | null }>(
  fronts: readonly T[],
  projectId: string
): T[] {
  if (!projectId) return [];
  return fronts.filter(front => front.projectId === projectId);
}

/** Somente ATIVAS e do projeto — é o que o RDO pode oferecer para seleção. */
export function selectableFrontsForProject<T extends { projectId?: string | null; status: string }>(
  fronts: readonly T[],
  projectId: string
): T[] {
  return filterFrontsByProject(fronts, projectId).filter(front => front.status === "ACTIVE");
}

/**
 * Trocou o Projeto: a Frente escolhida antes só continua válida se pertencer
 * ao novo Projeto. Projeto A com Frente do Projeto B nunca é aceito.
 */
export function keepFrontSelectionOnProjectChange<T extends { id: string; projectId?: string | null; status: string }>(
  fronts: readonly T[],
  projectId: string,
  currentFrontId: string
): string {
  if (!currentFrontId) return "";
  const stillValid = selectableFrontsForProject(fronts, projectId).some(front => front.id === currentFrontId);
  return stillValid ? currentFrontId : "";
}

/**
 * Projeto de uma Frente cadastrada antes do vínculo direto: vem da OS a que
 * ela estava presa. Sem OS correspondente, o projeto fica indefinido e a
 * Frente não aparece em nenhum projeto até ser editada.
 */
export function resolveFrontProjectId(
  front: { projectId?: string | null; workOrderId?: string | null },
  workOrders: readonly { id: string; projectId: string | null }[]
): string | null {
  if (front.projectId) return front.projectId;
  if (!front.workOrderId) return null;
  return workOrders.find(wo => wo.id === front.workOrderId)?.projectId ?? null;
}
